<script>
  import { Link } from 'svelte-routing';
  import { authStore } from '../authStore';
  import { getApiUrl } from '../api';
  import { getRelativeTime, formatDate, getCurrencySymbol, getBestReward } from '../utils';
  import ReportModal from './ReportModal.svelte';
  import { t } from '../i18nStore';
  import { joinedChallenges } from '../achievementStore';
  import LogTransactionModal from './LogTransactionModal.svelte';
  import { authFetch } from '../authService';

  export let promo;
  export let showStartDate = true;

  let currentUser = null;
  let favMessage = '';
  let challengeMessage = '';
  let currency = '$';
  
  // Modals
  let showReportModal = false;
  let showLogModal = false;

  // Joined State
  let achievementId = null;

  let isAdmin = false;

  authStore.subscribe(value => {
    currentUser = value.user;
    isAdmin = value.isAdmin;
  });

  // Check if current user has joined this promo
  joinedChallenges.subscribe(map => {
      if (promo && map[promo._id]) {
          achievementId = map[promo._id];
      } else {
          achievementId = null;
      }
  });

  $: currency = getCurrencySymbol(promo.countryCode);
  $: bestReward = getBestReward(promo);
  $: hasChallenge = (promo.targetNumTransactions > 0) || (promo.minimumTargetAccumulatedSpending > 0) || (promo.maximumTargetNumTransactions > 0) || (promo.maximumTargetAccumulatedSpending > 0);
  $: isAuthorOrAdmin = currentUser && (promo.createdByUserId === currentUser.idTokenClaims?.oid || isAdmin);

  async function handleFavourite() {
    favMessage = '';
    challengeMessage = '';
    const userId = currentUser?.idTokenClaims?.oid;
    if (!userId) {
      favMessage = 'You must be logged in to add a favourite.';
      return;
    }

    try {
      const response = await authFetch(getApiUrl('/favourites'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, promotionId: promo._id }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      favMessage = result.status;
      setTimeout(() => favMessage = '', 3000);
    } catch (err) {
      favMessage = `Error: ${err.message}`;
      setTimeout(() => favMessage = '', 3000);
    }
  }

  async function handleJoinChallenge() {
    if (!currentUser) return;
    try {
      const response = await authFetch(getApiUrl('/achievements'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.idTokenClaims.oid, promotionId: promo._id }),
      });
      const result = await response.json();
      
      if (!response.ok) {
          throw new Error(result.error || "Failed to join challenge");
      }
      
      // Update local store to show "Quick Log" button immediately
      joinedChallenges.update(map => {
          map[promo._id] = result.id || result.achievementId || true; 
          return map;
      });
      
      alert("Challenge joined! You can now use the ⚡ Log button to add transactions.");
      
    } catch (err) {
      alert(err.message);
    }
  }

  function handleReportSuccess() {
      promo.issueCount = (promo.issueCount || 0) + 1;
      alert("Thank you! Your report has been submitted.");
  }
  
  function openQuickLog() {
    // Prevent opening the modal if we only have the placeholder boolean 'true'
      if (achievementId === true || typeof achievementId === 'boolean' || achievementId === 'true') {
          alert("Challenge sync in progress. Please refresh the page to log your first transaction.");
          return;
      }

      showLogModal = true;
  }

  function copyCode() {
      if (promo.promoCode) {
          navigator.clipboard.writeText(promo.promoCode);
          alert("Code copied: " + promo.promoCode);
      }
  }
</script>

<!-- Modals -->
{#if currentUser}
  <ReportModal 
    bind:showModal={showReportModal} 
    targetId={promo._id} 
    targetType="promotion" 
    userId={currentUser.idTokenClaims.oid} 
    on:success={handleReportSuccess} 
  />
  
  <LogTransactionModal 
    bind:showModal={showLogModal} 
    achievementId={achievementId} 
    promotionName={promo.name} 
    on:success={() => alert("Transaction Logged!")}
    on:close={() => showLogModal = false}
  />
{/if}

<div class="promo-card">
  <div class="card-header">
    <div class="title-container">
      <Link to={`/promotions/detail/${promo._id}`} class="promo-title">
        <h3>{promo.name}</h3>
      </Link>
      {#if promo.requireRegistration}
        <span class="reg-badge" title="Registration Required">{$t('card.reg_req')}</span>
      {/if}
    </div>
    {#if promo.isBusiness}
      <span class="business-indicator" title="Posted by a Business Account">★ {$t('card.business_post')}</span>
    {/if}
  </div>
  
  <!-- Promo Code Display -->
  {#if promo.promoCode}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-interactive-supports-focus -->
    <div class="code-container" on:click={copyCode} title="Click to Copy" role="button">
        <span class="code-label">{$t('card.code')}:</span>
        <span class="code-value">{promo.promoCode}</span>
        <span class="copy-icon">📋</span>
    </div>
  {/if}

  <p class="description">{promo.description}</p>

  <div class="details-grid">
    {#if showStartDate}
      <div><strong>{$t('card.starts')}:</strong> {formatDate(promo.startDate)}</div>
    {/if}
    <div><strong>{$t('card.ends')}:</strong> {formatDate(promo.endDate)}</div>

    {#if promo.minimumSpending != null && promo.minimumSpending > 0}
      <div><strong>{$t('card.min_spend')}:</strong> {currency}{promo.minimumSpending.toFixed(2)}</div>
    {/if}
    
    {#if promo.minimumTargetAccumulatedSpending != null && promo.minimumTargetAccumulatedSpending > 0}
      <div class="target-spend"><strong>{$t('card.target_spend')}:</strong> {currency}{promo.minimumTargetAccumulatedSpending.toFixed(2)}</div>
    {/if}
    {#if promo.maximumTargetNumTransactions != null && promo.maximumTargetNumTransactions > 0}
      <div><strong>{$t('card.max_target_transactions')}:</strong> {promo.maximumTargetNumTransactions}</div>
    {/if}
    {#if promo.maximumTargetAccumulatedSpending != null && promo.maximumTargetAccumulatedSpending > 0}
      <div class="target-spend"><strong>{$t('card.max_target_spend')}:</strong> {currency}{promo.maximumTargetAccumulatedSpending.toFixed(2)}</div>
    {/if}

    {#if promo.calculatedReward}
        <!-- PRIORITY DISPLAY: Show specific calculated reward if user searched with spending -->
        <div class="calc-reward">
            <strong>{$t('card.your_reward')}:</strong> 
            <span class="highlight">{currency}{promo.calculatedReward.amount} {promo.calculatedReward.unit}</span>
            {#if promo.calculatedReward.remarks}
                <div class="remarks">({promo.calculatedReward.remarks})</div>
            {/if}
        </div>
    {:else if bestReward}
      {#if bestReward.type === 'Percentage'}
        <div class="reward percent">
          <strong>{$t('card.up_to')}:</strong> {bestReward.value.toFixed(2)}% {$t('card.reward')}
        </div>
      {:else if bestReward.type === 'Fixed'}
        <div class="reward fixed">
          <strong>{$t('card.up_to')}:</strong> {currency}{bestReward.value.toFixed(2)} {$t('card.reward')}
        </div>
      {:else if bestReward.type === 'TierFixed'}
        <div class="reward fixed">
          <strong>{$t('card.up_to')}:</strong> {bestReward.value} {bestReward.unit}
        </div>
      {/if}
    {/if}
  </div>

  <div class="tags">
    {#if promo.selectedMember}
      <span class="tag-exclusive">{$t('card.selected_member')}</span>
    {/if}
    {#if promo.isOnline}<span>{$t('card.online')}</span>{/if}
    {#if promo.isInStore}<span>{$t('card.in_store')}</span>{/if}
    {#if promo.nearestLocation}
      <span class="tag-location" title={promo.nearestLocation.address || ''}>
        📍 {promo.nearestLocation.placeName || promo.nearestLocation.storeName || ''}
        ({promo.nearestLocation.distanceMeters < 1000 ? `${Math.round(promo.nearestLocation.distanceMeters)}m` : `${(promo.nearestLocation.distanceMeters / 1000).toFixed(1)}km`})
      </span>
    {/if}
    {#each (promo.categories || []) as category}
      <span class="tag-category">{category}</span>
    {/each}
  </div>

  <div class="card-footer">
    <div class="author-info">
      <span>{$t('card.last_modified')} <strong>{promo.createdByUserName || 'Unknown'}</strong></span>
      <span class="time-ago">• {getRelativeTime(promo.lastUpdated)}</span>
    </div>
    
    <div class="actions-row">
      <div class="stats-group">
          <!-- Issue Count Button -->
          <button class="stat-btn warning" title="Report Issue" on:click={() => currentUser ? showReportModal = true : alert("Please log in.")}>
            ⚠️ {promo.issueCount || 0}
          </button>
      </div>
      
      <div class="actions">
        {#if currentUser}
          <!-- Quick Log vs Join Challenge -->
          {#if achievementId}
             <button class="action-btn log-btn" on:click={openQuickLog}>⚡ {$t('card.log')}</button>
          {:else if hasChallenge}
             <button class="action-btn challenge-btn" on:click={handleJoinChallenge}>{$t('card.join')}</button>
          {/if}
          
          <button class="action-btn favourite-btn" on:click={handleFavourite}>
            {$t('card.favourite')} ({promo.favouriteCount || 0})
          </button>
          {#if isAuthorOrAdmin}
            <Link to={`/edit/${promo._id}`} class="action-btn edit-btn">{$t('card.edit')}</Link>
          {/if}
        {/if}
      </div>
    </div>
  </div>
  
  {#if favMessage}<p class="message">{favMessage}</p>{/if}
  {#if challengeMessage}<p class="message success">{challengeMessage}</p>{/if}
</div>

<style>
  .promo-card {
    width: 100%; box-sizing: border-box; background: #fff; border: 1px solid #dee2e6;
    border-radius: 8px; padding: 1.5rem; text-align: left;
    box-shadow: 0 4px 6px rgba(0,0,0,0.05); display: flex; flex-direction: column;
    overflow: hidden;
  }
  .promo-title { text-decoration: none; color: inherit; }
  .promo-title h3:hover { color: #0056b3; }
  .card-header { display: flex; justify-content: space-between; align-items: flex-start; }
  .title-container { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .card-header h3 { margin-top: 0; margin-bottom: 0.5rem; margin-right: 0; }
  .business-indicator {
    background: #ffc107; color: #333; padding: 3px 10px; border-radius: 12px;
    font-size: 0.8rem; font-weight: bold; white-space: nowrap; flex-shrink: 0;
  }
  .reg-badge {
      background-color: #17a2b8; color: white; padding: 2px 8px; border-radius: 4px;
      font-size: 0.75rem; font-weight: bold; text-transform: uppercase; white-space: nowrap;
      margin-bottom: 0.5rem;
  }
  
  .code-container {
      background-color: #f0f4f8; border: 1px dashed #007bff; padding: 0.4rem 0.8rem;
      border-radius: 4px; display: inline-flex; align-items: center; gap: 0.5rem;
      margin-bottom: 0.5rem; cursor: pointer; width: fit-content; font-size: 0.9rem;
      transition: background-color 0.2s;
  }
  .code-container:hover { background-color: #e2e6ea; }
  .code-label { color: #666; font-weight: 500; }
  .code-value { font-weight: bold; color: #333; letter-spacing: 0.5px; }
  .copy-icon { font-size: 1rem; }

  .description { flex-grow: 1; color: #6c757d; }
  .details-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.5rem; font-size: 0.9rem; margin: 1rem 0;
  }
  .reward { font-weight: bold; }
  .percent { color: #28a745; }
  .fixed { color: #17a2b8; }
  .target-spend { font-weight: bold; color: #6610f2; }
  
  .calc-reward { 
      background: #e8f5e9; padding: 0.5rem; border-radius: 4px; border: 1px solid #28a745; 
      color: #155724; font-size: 0.95rem; margin-top: 0.5rem; grid-column: 1 / -1; 
  }
  .highlight { font-weight: 800; font-size: 1.1rem; }
  .remarks { font-size: 0.8rem; color: #555; font-style: italic; }
  
  .tags { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1rem; align-items: center;}
  .tags span {
    background-color: #e9ecef; color: #495057; padding: 3px 10px;
    border-radius: 12px; font-size: 0.8rem;
  }
  .tag-exclusive { background-color: #c52d3a; color: white; font-weight: bold; }
  .tag-category { background-color: #d1ecf1; color: #0c5460; }
  .tag-location { background-color: #e0f2fe !important; color: #0369a1 !important; font-weight: 600; border: 1px solid #bae6fd; }
  
  .card-footer {
    border-top: 1px solid #eee; padding-top: 1rem; margin-top: auto;
    display: flex; flex-direction: column; gap: 0.5rem;
  }
  .author-info { font-size: 0.8rem; color: #6c757d; display: flex; gap: 0.3rem; align-items: center; flex-wrap: wrap;}
  .time-ago { font-style: italic; }
  
  .actions-row {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 0.5rem;
      flex-wrap: wrap;
      gap: 0.5rem;
  }
  .actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  
  .action-btn {
    padding: 5px 10px; border: none; border-radius: 4px; cursor: pointer;
    text-decoration: none; font-size: 0.9rem; color: white;
  }
  .favourite-btn { background-color: #007bff; }
  .challenge-btn { background-color: #28a745; }
  .log-btn { background-color: #fd7e14; color: white; font-weight: bold; }
  .log-btn:hover { background-color: #e36d0a; }
  .edit-btn { background-color: #6c757d; }
  
  .stat-btn {
      background: none; border: 1px solid #eee; padding: 2px 8px; border-radius: 12px; cursor: pointer; font-size: 0.9rem; color: #666;
  }
  .stat-btn:hover { background-color: #f8f9fa; }
  .stat-btn.warning:hover { background-color: #fff3cd; border-color: #ffecb5; color: #856404; }
  
  .message { text-align: center; font-size: 0.9rem; margin-top: 1rem; color: #6c757d; }
  .message.success { color: #28a745; font-weight: bold; }
</style>